import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import { CheckCircle, AlertCircle, Loader2, Copy, KeyRound, Building2, Home } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { formatStsTokenGroups } from '@/lib/stsToken';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [paymentStatus, setPaymentStatus] = useState<'loading' | 'success' | 'failed' | 'pending'>('loading');
  const [paymentData, setPaymentData] = useState<any>(null);
  const [stsToken, setStsToken] = useState<string | null>(null);
  const [unitLabel, setUnitLabel] = useState<string | null>(null);
  const [buildingName, setBuildingName] = useState<string | null>(null);

  const reference = searchParams.get('reference');

  useEffect(() => {
    if (!reference) {
      setPaymentStatus('failed');
      return;
    }

    let cancelled = false;

    const resolveUnitBuilding = async (meterId: string | null | undefined) => {
      if (!meterId || cancelled) return;
      const { data: meter } = await supabase
        .from('smart_meters')
        .select('apartment_id, apartments(apartment_number, buildings(name))')
        .eq('id', meterId)
        .maybeSingle();
      if (cancelled || !meter) return;
      const apt = (meter as any).apartments;
      if (apt?.apartment_number) setUnitLabel(String(apt.apartment_number));
      const bName = apt?.buildings?.name;
      if (bName) setBuildingName(String(bName));
    };

    const checkPaymentStatus = async () => {
      try {
        const { data: cp } = await supabase
          .from('clickpesa_payments')
          .select('id, amount, status, order_reference, sts_token_no, recharge_channel, recharge_status, meter_id')
          .eq('order_reference', reference)
          .maybeSingle();

        if (!cancelled && cp) {
          setPaymentData(cp);
          if (cp.sts_token_no) setStsToken(cp.sts_token_no);
          void resolveUnitBuilding(cp.meter_id);
          if (cp.status === 'completed' || cp.status === 'SUCCESS' || cp.status === 'success') {
            if (
              !cp.recharge_status ||
              String(cp.recharge_status).toLowerCase() === 'pending'
            ) {
              setPaymentStatus('pending');
              setTimeout(checkPaymentStatus, 3000);
              return;
            }
            setPaymentStatus('success');
            return;
          }
          if (cp.status === 'failed' || cp.status === 'FAILED') {
            setPaymentStatus('failed');
            return;
          }
          setPaymentStatus('pending');
          setTimeout(checkPaymentStatus, 3000);
          return;
        }

        const { data, error } = await supabase
          .from('mobile_money_payments')
          .select('*')
          .eq('reference', reference)
          .maybeSingle();

        if (error || !data) {
          if (!cancelled) setPaymentStatus('failed');
          return;
        }

        if (cancelled) return;
        setPaymentData(data);
        void resolveUnitBuilding((data as any).meter_id);

        if (data.status === 'completed') {
          setPaymentStatus('success');
          toast({
            title: 'Payment Successful!',
            description: `Your account has been topped up with TZS ${data.amount.toLocaleString()}`,
          });
        } else if (data.status === 'failed') {
          setPaymentStatus('failed');
        } else {
          setPaymentStatus('pending');
          setTimeout(checkPaymentStatus, 3000);
        }
      } catch (error) {
        console.error('Payment status check error:', error);
        if (!cancelled) setPaymentStatus('failed');
      }
    };

    checkPaymentStatus();
    return () => { cancelled = true; };
  }, [reference, toast]);

  const handleGoToDashboard = () => navigate('/user/dashboard');
  const handleGoToTopUp = () => navigate('/user/topup');

  const copyToken = async () => {
    if (!stsToken) return;
    try {
      await navigator.clipboard.writeText(stsToken);
      toast({ title: 'Token copied' });
    } catch {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  };

  const unitBuildingBlock =
    unitLabel || buildingName ? (
      <div className="mb-4 rounded-xl border border-border bg-muted/40 p-4 text-left space-y-2">
        {unitLabel && (
          <div className="flex items-center gap-2 text-sm">
            <Home className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Unit</span>
            <strong className="ml-auto">{unitLabel}</strong>
          </div>
        )}
        {buildingName && (
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Building</span>
            <strong className="ml-auto text-right">{buildingName}</strong>
          </div>
        )}
      </div>
    ) : null;

  const renderContent = () => {
    switch (paymentStatus) {
      case 'loading':
        return (
          <div className="text-center">
            <Loader2 size={64} className="text-blue-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Verifying Payment...</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">Please wait while we confirm your payment</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Confirmed!</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Your payment has been verified and your account has been topped up with TZS {paymentData?.amount?.toLocaleString()}
            </p>
            {unitBuildingBlock}
            {paymentData?.recharge_channel === 'chirpstack' && (
              <div className="mb-4 rounded-xl border border-green-300 bg-green-50 dark:bg-green-950/40 p-4 text-left">
                <div className="flex items-center gap-2 text-green-800 dark:text-green-100 font-semibold mb-2">
                  <CheckCircle className="h-5 w-5" />
                  Credit delivered to meter
                </div>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Your STS token was accepted by the meter over LoRaWAN.
                </p>
                {stsToken && (
                  <p className="font-mono text-sm tracking-wide break-all mt-2 text-muted-foreground">
                    {formatStsTokenGroups(stsToken)}
                  </p>
                )}
              </div>
            )}
            {stsToken && paymentData?.recharge_channel !== 'chirpstack' && (
              <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/40 p-4 text-left">
                <div className="flex items-center gap-2 text-amber-900 dark:text-amber-100 font-semibold mb-2">
                  <KeyRound className="h-5 w-5" />
                  Meter delivery failed — use keypad
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Payment succeeded, but the meter did not confirm the LoRaWAN token. Enter this token on your meter keypad.
                </p>
                <p className="font-mono text-lg sm:text-xl tracking-wide break-all text-foreground mb-3">
                  {formatStsTokenGroups(stsToken)}
                </p>
                <Button type="button" variant="outline" size="sm" onClick={copyToken}>
                  <Copy className="h-4 w-4 mr-1.5" /> Copy token
                </Button>
              </div>
            )}
            {paymentData?.recharge_channel === 'chirpstack' && !stsToken && (
              <p className="text-sm text-muted-foreground mb-4">Units were sent to your LoRaWAN meter.</p>
            )}
            <div className="space-y-3">
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-semibold"
              >
                Go to Dashboard
              </button>
              <button
                onClick={handleGoToTopUp}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold"
              >
                Top Up Again
              </button>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center">
            <Loader2 size={64} className="text-yellow-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Pending</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Your payment is being processed. Please complete the M-Pesa prompt on your phone.
            </p>
            {unitBuildingBlock}
            <p className="text-sm text-gray-500 mb-4">
              This page will update automatically when your payment is confirmed.
            </p>
            <button
              onClick={handleGoToDashboard}
              className="w-full py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-semibold"
            >
              Continue to Dashboard
            </button>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <AlertCircle size={64} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Failed</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {reference ? 'Unable to verify your payment. Please try again.' : 'Invalid payment reference.'}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleGoToTopUp}
                className="w-full py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-semibold"
              >
                Try Again
              </button>
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <MainLayout title="Payment Status">
      <div className="p-4 flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full">
          {renderContent()}

          {reference && (
            <div className="mt-6 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-500 text-center">
                Reference: {reference}
              </p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default PaymentSuccess;
